import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

/**
 * `penn_config.autoriv_configurator_items` admin surface.
 * Route key is the row's `id` (uuid) — `partNo` repeats across rows and is not
 * unique, so it can never key a request.
 */
export interface AutorivConfiguratorItem {
  id: string;
  partNo: string | null;
  itemName: string | null;
  itemDescription: string | null;
  product: string | null;
  subcategory: string | null;
  fastenerGroup: string | null;
  feederTracks: string | null;
  /** Decimal string, numeric(8,2). */
  forceKn: string | null;
  /** Decimal string, numeric(12,2). */
  priceEur: string | null;
  /** Decimal string, numeric(12,2). Read-only on this screen. */
  priceUsd: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedAutorivConfiguratorItems {
  data: AutorivConfiguratorItem[];
  pagination: PaginationMeta;
}

export interface AutorivConfiguratorItemOptions {
  /**
   * `A###` family code of every active MDS machine (A240-CP and A240-BI both
   * collapse to A240) — sourced from the products catalog, not from the item
   * rows, so an unused family is still selectable.
   */
  products: string[];
  subcategories: string[];
  fastenerGroups: string[];
  feederTracks: string[];
}

/** Filter token selecting rows whose attribute is NULL or empty. */
export const AUTORIV_BLANK_FILTER = "__blank__";

export type AutorivConfiguratorItemSortColumn =
  | "partNo"
  | "itemName"
  | "product"
  | "subcategory"
  | "fastenerGroup"
  | "feederTracks"
  | "forceKn"
  | "priceEur";

export interface ListAutorivConfiguratorItemsParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Exact value, or `AUTORIV_BLANK_FILTER` for the NULL/empty bucket. */
  subcategory?: string;
  /** Exact value, or `AUTORIV_BLANK_FILTER` for the NULL/empty bucket. */
  fastenerGroup?: string;
  sortBy?: AutorivConfiguratorItemSortColumn;
  sortOrder?: "asc" | "desc";
}

export interface CreateAutorivConfiguratorItemBody {
  partNo: string;
  itemName: string;
  itemDescription?: string | null;
  product: string;
  subcategory?: string | null;
  fastenerGroup?: string | null;
  feederTracks?: string | null;
  forceKn?: string | number | null;
  /**
   * The API requires AT LEAST ONE of priceEur / priceUsd. Each currency page
   * sends only its own column, leaving the sibling for the other page.
   */
  priceEur?: string | number | null;
  priceUsd?: string | number | null;
}

/** PATCH — send only the keys that changed. */
export type UpdateAutorivConfiguratorItemBody = Partial<
  CreateAutorivConfiguratorItemBody
>;

const BASE = "/v1/autoriv-configurator-items";

export async function listAutorivConfiguratorItems(
  params: ListAutorivConfiguratorItemsParams = {},
): Promise<PaginatedAutorivConfiguratorItems> {
  const res = await Api.get(BASE, { params });
  return unwrap<PaginatedAutorivConfiguratorItems>(res.data);
}

export async function getAutorivConfiguratorItem(
  id: string,
): Promise<AutorivConfiguratorItem> {
  const res = await Api.get(`${BASE}/${encodeURIComponent(id)}`);
  return unwrap<AutorivConfiguratorItem>(res.data);
}

export async function listAutorivConfiguratorItemOptions(): Promise<AutorivConfiguratorItemOptions> {
  const res = await Api.get(`${BASE}/options`);
  return unwrap<AutorivConfiguratorItemOptions>(res.data);
}

export async function createAutorivConfiguratorItem(
  body: CreateAutorivConfiguratorItemBody,
): Promise<AutorivConfiguratorItem> {
  const res = await Api.post(BASE, body);
  return unwrap<AutorivConfiguratorItem>(res.data);
}

export async function updateAutorivConfiguratorItem(
  id: string,
  body: UpdateAutorivConfiguratorItemBody,
): Promise<AutorivConfiguratorItem> {
  const res = await Api.patch(`${BASE}/${encodeURIComponent(id)}`, body);
  return unwrap<AutorivConfiguratorItem>(res.data);
}

export async function deleteAutorivConfiguratorItem(
  id: string,
): Promise<{ success: true }> {
  const res = await Api.delete(`${BASE}/${encodeURIComponent(id)}`);
  return unwrap<{ success: true }>(res.data);
}
